import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import DraftRoutes from './routes';
import '../../assets/css/editor.scss';
import './Draft.scss';
import { ElearningParamType } from '@/pages/Module/routes';
import { useThemeStyle } from '@/modules/hooks/useThemeStyle';
import DraftView from './DraftView';
import {
  useGetElearningBehaviourSettingsQuery,
  useGetElearningQuery,
  useGetFramesQuery,
  useGetHelperContentQuery,
  useGetHelperFaqQuery,
  useGetQuestionsQuery,
  useGetQuizQuery,
  useGetResourcesQuery,
} from '@/modules/services';
import { useErrorHandler } from 'react-error-boundary';
import { processQuestion } from '@/lib/util';
import { useResumeElearning } from '@/modules/hooks/useResumeElearning';
import { useModuleStyle } from '@/modules/hooks/useModuleStyle';

const Draft = (): ReactElement => {
  const history = useHistory();
  const handleError = useErrorHandler();
  const { elearningId, frameId } = useParams<ElearningParamType>();
  const [isQuizReady, setIsQuizReady] = useState(false);
  const [currentFrameId, setCurrentFrameId] = useState(frameId || '');
  const elearning = useGetElearningQuery(elearningId);
  const frames = useGetFramesQuery({ elearningId });
  const quiz = useGetQuizQuery(elearningId);
  const questions = useGetQuestionsQuery(
    {
      quizId: `${quiz.data?.id}`,
    },
    {
      skip: !quiz.data?.id || !quiz.data,
    },
  );
  const helperContent = useGetHelperContentQuery('');
  const helperFaq = useGetHelperFaqQuery('');
  const resources = useGetResourcesQuery({ elearningId });
  const initialFrameId = useRef(frameId);
  const {
    data: elearningBehaviourSettingsData,
    isLoading: elearningBehaviourSettingsIsLoading,
  } = useGetElearningBehaviourSettingsQuery();
  const isBookmarkSettingsEnabled =
    elearningBehaviourSettingsData &&
    elearningBehaviourSettingsData.elearningBookmarkEnable;
  const showResumeModal = initialFrameId.current && isBookmarkSettingsEnabled;

  useThemeStyle(elearning.data?.theme);
  useModuleStyle(elearning.data);

  const ResumeModalView: null | JSX.Element = useResumeElearning(
    setCurrentFrameId,
    elearning?.data?.name,
    showResumeModal,
  );

  useEffect(() => {
    if (!elearningBehaviourSettingsIsLoading && !showResumeModal) {
      setCurrentFrameId('');
    }
  }, [elearningBehaviourSettingsIsLoading, showResumeModal]);

  if (
    elearning.isError ||
    helperContent.isError ||
    helperFaq.isError ||
    resources.isError ||
    quiz.isError ||
    questions.isError
  ) {
    handleError(elearning.error);
  }

  if (
    elearning.isLoading ||
    frames.isLoading ||
    helperContent.isLoading ||
    helperFaq.isLoading ||
    resources.isLoading ||
    quiz.isLoading ||
    questions.isLoading ||
    elearningBehaviourSettingsIsLoading ||
    !elearning.data ||
    !frames.data ||
    !quiz.data ||
    !questions.data
  ) {
    return <div className="loading" />;
  }

  const playerHandlerState = {
    questionsToAsk: quiz.data.quizMetadata.questionNo,
    questionsAsked: 0,
    correctAnswers: 0,
    modifiedQuestions: questions.data,
    questionsCompleted: [],
  };

  const playerHandlers = {
    submitResult: (result: any, callback: () => void) => {
      // @todo : submit result to TMS
      callback();
    },
    submitQuestion: (answer: any, callback: (payload: any) => void) => {
      // @todo: The below needs to be checked
      const nextPayload = processQuestion(answer, playerHandlerState);
      callback(nextPayload);
    },
    getQuestions: () => {
      playerHandlerState.questionsAsked = 0;
      playerHandlerState.correctAnswers = 0;
      playerHandlerState.modifiedQuestions = questions.data
        ? questions.data
        : [];
      playerHandlerState.questionsCompleted = [];
    },
    submitAssessment: (quizData: any, callback: (payload: any) => void) => {
      const divisor = quiz.data?.quizMetadata.questionNo || 1;
      const score = Math.round(
        (playerHandlerState.correctAnswers / divisor) * 100,
      );

      callback({ score });
    },
    startAssessment: () => {
      setIsQuizReady(true);
    },
    moveToFrame: (nextFrameId: string) => {
      setCurrentFrameId(nextFrameId);
      const nextPath = generatePath(DraftRoutes.path, {
        elearningId,
        frameId: nextFrameId,
      });
      history.push(nextPath);
    },
  };

  const playerHelper = {
    content: helperContent.data,
    faq: helperFaq.data,
  };

  const quizData = {
    ...quiz.data,
    questions: questions.data,
  };

  return (
    <>
      {ResumeModalView}
      <DraftView
        isQuizReady={isQuizReady}
        elearning={elearning.data}
        frames={frames.data}
        currentFrameId={currentFrameId || frames.data[0].id}
        playerHelper={playerHelper}
        resources={resources.data || []}
        quiz={quizData}
        handlers={playerHandlers}
        elearningBehaviourSettings={elearningBehaviourSettingsData}
      />
    </>
  );
};

export default Draft;
